import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { projectService, Project, SubmitProjectData, ProjectUpdateData, ProjectFilters } from '../services/projectService';

// Types
interface ProjectState {
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  error: string | null;
  filters: ProjectFilters;
  totalCount: number;
}

interface ProjectContextType extends ProjectState {
  fetchProjects: (filters?: ProjectFilters) => Promise<void>;
  fetchProject: (id: string) => Promise<void>;
  submitProject: (data: SubmitProjectData) => Promise<Project>;
  updateProject: (id: string, data: ProjectUpdateData) => Promise<Project>;
  approveProject: (id: string, autoTokenize?: boolean, tokenStandard?: string) => Promise<void>;
  tokenizeProject: (id: string) => Promise<void>;
  setFilters: (filters: ProjectFilters) => void;
  clearError: () => void;
  clearCurrentProject: () => void;
}

// Action types
type ProjectAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_PROJECTS'; payload: { projects: Project[]; totalCount: number } }
  | { type: 'SET_CURRENT_PROJECT'; payload: Project | null }
  | { type: 'ADD_PROJECT'; payload: Project }
  | { type: 'UPDATE_PROJECT'; payload: Project }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_FILTERS'; payload: ProjectFilters }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR_CURRENT_PROJECT' };

// Initial state
const initialState: ProjectState = {
  projects: [],
  currentProject: null,
  loading: false,
  error: null,
  filters: {},
  totalCount: 0,
};

// Reducer
const projectReducer = (state: ProjectState, action: ProjectAction): ProjectState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_PROJECTS':
      return { 
        ...state, 
        projects: action.payload.projects, 
        totalCount: action.payload.totalCount 
      };
    case 'SET_CURRENT_PROJECT':
      return { ...state, currentProject: action.payload };
    case 'ADD_PROJECT':
      return { 
        ...state, 
        projects: [action.payload, ...state.projects],
        totalCount: state.totalCount + 1
      };
    case 'UPDATE_PROJECT':
      return {
        ...state,
        projects: state.projects.map(p => 
          p.id === action.payload.id ? action.payload : p
        ),
        currentProject: state.currentProject?.id === action.payload.id 
          ? action.payload 
          : state.currentProject
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: action.payload };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'CLEAR_CURRENT_PROJECT':
      return { ...state, currentProject: null };
    default:
      return state;
  }
};

// Context
const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

// Provider component
interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(projectReducer, initialState);

  // Fetch projects
  const fetchProjects = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      const response = await projectService.getProjects();
      dispatch({ 
        type: 'SET_PROJECTS', 
        payload: { 
          projects: response.projects, 
          totalCount: response.total_count 
        } 
      });
    } catch (error) {
      console.error('Error fetching projects:', error);
      dispatch({ 
        type: 'SET_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to fetch projects' 
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Fetch single project
  const fetchProject = async (id: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const project = await projectService.getProject(id);
      dispatch({ type: 'SET_CURRENT_PROJECT', payload: project });
    } catch (error) {
      console.error('Failed to fetch project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch project';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Submit project
  const submitProject = async (data: SubmitProjectData): Promise<Project> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const response = await projectService.submitProject(data);
      const newProject = await projectService.getProject(response.id);
      dispatch({ type: 'ADD_PROJECT', payload: newProject });
      return newProject;
    } catch (error) {
      console.error('Failed to submit project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit project';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Update project
  const updateProject = async (id: string, data: ProjectUpdateData): Promise<Project> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      await projectService.updateProject(id, data);
      const updatedProject = await projectService.getProject(id);
      dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject });
      return updatedProject;
    } catch (error) {
      console.error('Failed to update project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update project';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Approve project
  const approveProject = async (id: string, autoTokenize: boolean = false, tokenStandard: string = 'standard') => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      await projectService.approveProject(id, autoTokenize, tokenStandard);
      const updatedProject = await projectService.getProject(id);
      dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject });
    } catch (error) {
      console.error('Failed to approve project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to approve project';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Tokenize project
  const tokenizeProject = async (id: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      await projectService.tokenizeProject(id);
      const updatedProject = await projectService.getProject(id);
      dispatch({ type: 'UPDATE_PROJECT', payload: updatedProject });
    } catch (error) {
      console.error('Failed to tokenize project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to tokenize project';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Set filters
  const setFilters = (filters: ProjectFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Clear current project
  const clearCurrentProject = () => {
    dispatch({ type: 'CLEAR_CURRENT_PROJECT' });
  };

  const value: ProjectContextType = {
    ...state,
    fetchProjects,
    fetchProject,
    submitProject,
    updateProject,
    approveProject,
    tokenizeProject,
    setFilters,
    clearError,
    clearCurrentProject,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

// Hook to use project context
export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}; 